import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  clearStoredSession,
  fetchSession,
  loginWithPassword,
  logoutRemote,
  readStoredSession,
  setUnauthorizedHandler,
  LoginResult,
} from './adminApi';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => Promise<LoginResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(readStoredSession()));

  const endSession = useCallback(() => {
    clearStoredSession();
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(endSession);
    return () => setUnauthorizedHandler(null);
  }, [endSession]);

  useEffect(() => {
    if (!readStoredSession()) {
      endSession();
      return;
    }
    void fetchSession()
      .then((ok) => {
        if (ok) setIsAuthenticated(true);
      })
      .catch(() => {
        // Operational/network failure is not a 401; keep stored session.
      });
  }, [endSession]);

  const login = async (password: string): Promise<LoginResult> => {
    const result = await loginWithPassword(password);
    if (result === 'ok') setIsAuthenticated(true);
    return result;
  };

  const logout = () => {
    endSession();
    void logoutRemote();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
